// This file handles Xendit provider account management
// Providers need to provide bank account details for payouts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';

// ================ Async thunks ================ //

///////////////////////////
// Create Provider Account //
///////////////////////////
const createProviderAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  const {
    bankCode,
    accountNumber,
    accountHolderName,
  } = params;

  // Store provider bank account details in user's protected data
  return sdk.currentUser
    .updateProfile(
      {
        protectedData: {
          xenditBankAccount: {
            bankCode,
            accountNumber,
            accountHolderName,
            verified: false, // Will be verified on first successful payout
            createdAt: new Date().toISOString(),
          },
        },
      },
      {
        expand: true,
      }
    )
    .then(response => {
      const user = response.data.data;
      return user.attributes.profile.protectedData.xenditBankAccount;
    })
    .catch(err => {
      const e = storableError(err);
      log.error(err, 'create-xendit-provider-account-failed');
      return rejectWithValue(e);
    });
};

export const createProviderAccountThunk = createAsyncThunk(
  'xenditProviderAccount/createProviderAccount',
  createProviderAccountPayloadCreator
);

// Backward compatible wrapper function
export const createProviderAccount = params => dispatch => {
  return dispatch(createProviderAccountThunk(params)).unwrap();
};

///////////////////////////
// Update Provider Account //
///////////////////////////
const updateProviderAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  const {
    bankCode,
    accountNumber,
    accountHolderName,
  } = params;

  return sdk.currentUser
    .updateProfile(
      {
        protectedData: {
          xenditBankAccount: {
            bankCode,
            accountNumber,
            accountHolderName,
            verified: false,
            updatedAt: new Date().toISOString(),
          },
        },
      },
      {
        expand: true,
      }
    )
    .then(response => {
      const user = response.data.data;
      return user.attributes.profile.protectedData.xenditBankAccount;
    })
    .catch(err => {
      const e = storableError(err);
      log.error(err, 'update-xendit-provider-account-failed');
      return rejectWithValue(e);
    });
};

export const updateProviderAccountThunk = createAsyncThunk(
  'xenditProviderAccount/updateProviderAccount',
  updateProviderAccountPayloadCreator
);

// Backward compatible wrapper function
export const updateProviderAccount = params => dispatch => {
  return dispatch(updateProviderAccountThunk(params)).unwrap();
};

//////////////////////////
// Fetch Provider Account //
//////////////////////////
const fetchProviderAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .show({
      include: ['profileImage'],
    })
    .then(response => {
      const user = response.data.data;
      return user.attributes.profile.protectedData?.xenditBankAccount || null;
    })
    .catch(err => {
      const e = storableError(err);
      log.error(err, 'fetch-xendit-provider-account-failed');
      return rejectWithValue(e);
    });
};

export const fetchProviderAccountThunk = createAsyncThunk(
  'xenditProviderAccount/fetchProviderAccount',
  fetchProviderAccountPayloadCreator
);

// Backward compatible wrapper function
export const fetchProviderAccount = params => dispatch => {
  return dispatch(fetchProviderAccountThunk(params)).unwrap();
};

// ================ Slice ================ //

const xenditProviderAccountSlice = createSlice({
  name: 'xenditProviderAccount',
  initialState: {
    createProviderAccountInProgress: false,
    createProviderAccountError: null,
    updateProviderAccountInProgress: false,
    updateProviderAccountError: null,
    fetchProviderAccountInProgress: false,
    fetchProviderAccountError: null,
    providerAccount: null,
    providerAccountFetched: false,
  },
  reducers: {
    providerAccountClearError: state => {
      return {
        createProviderAccountInProgress: false,
        createProviderAccountError: null,
        updateProviderAccountInProgress: false,
        updateProviderAccountError: null,
        fetchProviderAccountInProgress: false,
        fetchProviderAccountError: null,
        providerAccount: null,
        providerAccountFetched: false,
      };
    },
    updateXenditProviderAccount: (state, action) => {
      state.providerAccount = action.payload;
      state.providerAccountFetched = true;
    },
  },
  extraReducers: builder => {
    builder
      // Create Provider Account cases
      .addCase(createProviderAccountThunk.pending, state => {
        state.createProviderAccountError = null;
        state.createProviderAccountInProgress = true;
      })
      .addCase(createProviderAccountThunk.fulfilled, (state, action) => {
        state.createProviderAccountInProgress = false;
        state.providerAccount = action.payload;
        state.providerAccountFetched = true;
      })
      .addCase(createProviderAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.createProviderAccountError = action.payload;
        state.createProviderAccountInProgress = false;
      })
      // Update Provider Account cases
      .addCase(updateProviderAccountThunk.pending, state => {
        state.updateProviderAccountError = null;
        state.updateProviderAccountInProgress = true;
      })
      .addCase(updateProviderAccountThunk.fulfilled, (state, action) => {
        state.updateProviderAccountInProgress = false;
        state.providerAccount = action.payload;
        state.providerAccountFetched = true;
      })
      .addCase(updateProviderAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.updateProviderAccountError = action.payload;
        state.updateProviderAccountInProgress = false;
      })
      // Fetch Provider Account cases
      .addCase(fetchProviderAccountThunk.pending, state => {
        state.fetchProviderAccountError = null;
        state.fetchProviderAccountInProgress = true;
      })
      .addCase(fetchProviderAccountThunk.fulfilled, (state, action) => {
        state.fetchProviderAccountInProgress = false;
        state.providerAccount = action.payload;
        state.providerAccountFetched = true;
      })
      .addCase(fetchProviderAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.fetchProviderAccountError = action.payload;
        state.fetchProviderAccountInProgress = false;
      });
  },
});

// Export the action creators
export const {
  providerAccountClearError,
  updateXenditProviderAccount,
} = xenditProviderAccountSlice.actions;

export default xenditProviderAccountSlice.reducer;
